<!--
  The support thread: a chat between this business and PlanUp support, opened from the headset
  button in the header. What the user types goes out over the business's own linked whatsapp line
  to the support number (+972 54-335-7624) and is stored in the shared planup.support_message
  table, which is what the root-admin inbox (/root-admin/support) reads. Replies come back the
  same way - typed on the support phone and captured by baileys, or typed in the admin page - and
  arrive here over the socket ('supportMessage'), with polling as the fallback while the modal is
  open (localhost has no socket at all).

  A screenshot can ride along: the camera button captures the visible screen (html2canvas, the
  modal itself left out), and an image can also be pasted (Ctrl+V after PrtScn) or picked from disk.

  One instance, mounted in App.vue for logged-in business users. It owns the unread count
  ($store.state.supportUnread) that the header buttons display.
-->
<template>

    <Modal ref="modal"
           :hideFooter="true"
           :hideHeader="true"
           class="supportModal">

        <template v-slot:body>

            <div class="supportChat">


                <div class="supportChatHeader">

                    <div class="supportChatHeaderIcon">
                        <i class="fa-solid fa-headset"></i>
                    </div>

                    <div class="supportChatHeaderText">
                        <div class="supportChatTitle">תמיכה</div>
                        <div class="supportChatSubtitle">{{ subtitle }}</div>
                    </div>

                    <button type="button"
                            class="btn-close supportChatClose"
                            data-bs-dismiss="modal"></button>

                </div>


                <div ref="messageContainer"
                     class="supportChatMessages custom-scrollbar">


                    <div v-if="loading && !messages.length"
                         class="supportChatCenter">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                    </div>


                    <div v-if="!loading && !messages.length"
                         class="supportChatEmpty">
                        <div class="supportChatEmptyIcon">👋</div>
                        <div class="supportChatEmptyTitle">שלום {{ firstName }}, איך נוכל לעזור?</div>
                        <div class="supportChatEmptyText">
                            כתבו לנו מה קרה - אפשר גם לצרף צילום מסך כדי שנראה בדיוק מה אתם רואים.
                            נחזור אליכם כאן ובוואטסאפ.
                        </div>
                    </div>


                    <div v-for="message in messages"
                         :key="message.id || message.localKey"
                         class="supportBubbleRow"
                         :class="message.fromSupport ? 'fromSupport' : 'fromUser'">

                        <div class="supportBubble">

                            <div v-if="message.fromSupport"
                                 class="supportBubbleSender">
                                <i class="fa-solid fa-headset"></i>
                                תמיכת PlanUp
                            </div>

                            <a v-if="message.attachmentUrl"
                               :href="message.attachmentUrl"
                               target="_blank"
                               class="supportBubbleImage">
                                <img :src="message.attachmentUrl">
                            </a>

                            <div v-if="message.message"
                                 class="supportBubbleText">{{ message.message }}</div>

                            <div class="supportBubbleMeta">

                                <span class="supportBubbleTime">{{ timeLabel(message.created) }}</span>

                                <span v-if="!message.fromSupport"
                                      class="supportBubbleStatus">

                                    <i v-if="message.sending"
                                       class="fa-solid fa-spinner fa-spin"
                                       title="שולח"></i>

                                    <span v-else-if="message.failed"
                                          class="supportStatusFailed">לא נשלח</span>

                                    <i v-else-if="message.gotWhatsappAck"
                                       class="fa-solid fa-check-double supportStatusDelivered"
                                       title="נמסר בוואטסאפ"></i>

                                    <i v-else-if="message.sentViaWhatsapp"
                                       class="fa-solid fa-check"
                                       title="נשלח בוואטסאפ"></i>

                                    <span v-else-if="message.whatsappError"
                                          class="supportStatusSystem"
                                          :title="'לא נשלח בוואטסאפ: ' + message.whatsappError">התקבל במערכת</span>

                                    <i v-else
                                       class="fa-solid fa-check"
                                       title="נשלח"></i>

                                </span>

                            </div>

                        </div>

                    </div>

                </div>


                <div class="supportChatComposer">

                    <div v-if="screenshot"
                         class="supportScreenshotPreview">
                        <img :src="screenshot">
                        <span class="supportScreenshotLabel">צילום מסך מצורף</span>
                        <span class="supportScreenshotRemove"
                              title="הסרה"
                              @click="screenshot = null">
                            <i class="fa-solid fa-xmark"></i>
                        </span>
                    </div>

                    <div class="supportComposerRow">

                        <textarea ref="input"
                                  v-model="text"
                                  rows="1"
                                  placeholder="כתבו כאן את השאלה או הבעיה..."
                                  @keydown.enter.exact.prevent="send"
                                  @paste="onPaste"
                                  @input="autoGrow"></textarea>

                        <button type="button"
                                class="supportComposerIconBtn"
                                :class="{ active: capturing }"
                                :title="capturing ? 'מצלם את המסך...' : 'צילום המסך הנוכחי'"
                                :disabled="capturing"
                                @click="captureScreenshot">
                            <i :class="capturing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-camera'"></i>
                        </button>

                        <button type="button"
                                class="supportComposerIconBtn"
                                title="צירוף תמונה מהמחשב"
                                @click="$refs.fileInput.click()">
                            <i class="fa-solid fa-paperclip"></i>
                        </button>

                        <input ref="fileInput"
                               type="file"
                               accept="image/*"
                               style="display:none"
                               @change="onFilePicked">

                        <button type="button"
                                class="btn btn-primary supportSendBtn"
                                :disabled="sending || (!text.trim() && !screenshot)"
                                @click="send">
                            <i class="fa-solid fa-paper-plane"></i>
                        </button>

                    </div>

                    <div class="supportComposerHint">
                        Enter לשליחה · Shift+Enter לשורה חדשה · אפשר להדביק צילום מסך (Ctrl+V)
                    </div>

                </div>


            </div>

        </template>

    </Modal>

</template>
<script>

import axiosV2, { apiPost } from '../../core/http/axios_api_v2';
import SupportHandler from '../../modules/SupportHandler';

export default {

    name: 'SupportModal',

    data() {
        return {
            messages: [],
            loading: false,
            text: '',
            screenshot: null,       // jpeg data url waiting to be sent
            capturing: false,
            sending: false,
            isOpen: false,
            pollTimer: null,
            unreadTimer: null,
        }
    },

    computed: {

        firstName() {
            let user = this.$store.state.user;
            let name = (user && user.name) ? String(user.name).trim() : '';
            return name.split(' ')[0] || '';
        },

        subtitle() {

            //whatsappStatus holds "05... מחובר" when the business's line is linked
            let linked = typeof this.$store.state.whatsappStatus == 'string' && this.$store.state.whatsappStatus.includes('0');

            if (linked)
                return 'ההודעות נשלחות בוואטסאפ מהקו של העסק · נחזור אליכם בהקדם';

            return 'הוואטסאפ של העסק אינו מחובר - ההודעה תגיע לצוות התמיכה דרך המערכת';
        },

    },

    mounted() {

        this.$root.$on('openSupportModal', this.open);
        this.$root.$on('supportMessageArrived', this.onSocketMessage);

        //bootstrap modal events - the modal element is this component's root
        this.$el.addEventListener('shown.bs.modal', this.onShown);
        this.$el.addEventListener('hidden.bs.modal', this.onHidden);

        //the badge: what came in while nobody was looking. The socket bumps it live; the poll is
        //for reloads and for localhost, where there is no socket
        this.refreshUnread();
        this.unreadTimer = setInterval(this.refreshUnread, 60000);

    },

    beforeDestroy() {

        this.$root.$off('openSupportModal', this.open);
        this.$root.$off('supportMessageArrived', this.onSocketMessage);

        this.$el.removeEventListener('shown.bs.modal', this.onShown);
        this.$el.removeEventListener('hidden.bs.modal', this.onHidden);

        clearInterval(this.unreadTimer);
        this.stopPolling();

    },

    methods: {

        timeLabel(created) {
            return SupportHandler.timeLabel(created);
        },

        open() {

            this.isOpen = true;
            this.$refs.modal.show();
            this.load();

        },

        onShown() {

            this.isOpen = true;
            this.startPolling();
            this.scrollToBottom();

            this.$nextTick(() => {
                if (this.$refs.input)
                    this.$refs.input.focus();
            });

        },

        onHidden() {

            this.isOpen = false;
            this.stopPolling();

        },

        startPolling() {

            this.stopPolling();
            this.pollTimer = setInterval(this.load, 8000);

        },

        stopPolling() {

            if (this.pollTimer)
                clearInterval(this.pollTimer);

            this.pollTimer = null;

        },

        refreshUnread() {

            axiosV2('support/unread').then(res => {

                if (!res || !res.data || typeof res.data.count != 'number')
                    return;

                this.$store.state.supportUnread = this.isOpen ? 0 : res.data.count;

                //support may write first (the admin inbox's שיחה חדשה) - a message that came in
                //while nobody was looking gets a toast on the next load, not only the badge
                if (!this.isOpen && res.data.count > 0 && res.data.last)
                    this.toastSupportMessage(res.data.last);

            }).catch(() => { });

        },

        //once per message: the socket and the unread poll can both bring the same one. Message
        //ids only grow (one shared table), so the newest id toasted is all there is to remember.
        toastSupportMessage(row) {

            if (!row || !row.id)
                return;

            let toasted = 0;

            try {
                toasted = +localStorage.getItem('planupSupportToastedId') || 0;
            } catch (e) { }

            if (+row.id <= toasted)
                return;

            try {
                localStorage.setItem('planupSupportToastedId', String(row.id));
            } catch (e) { }

            let preview = row.message ? String(row.message).slice(0, 80) : ((row.attachmentUrl || row.hasPicture) ? '📷 תמונה' : '');

            //long enough to be noticed on a page that is still loading
            this.$notify({
                type: 'supportmessage',
                title: 'hidden',
                duration: 12000,
                text: `<div class="supportMsgIcon"><i class="fa-solid fa-headset"></i></div><div class="whatsappMsgBody"><b>הודעה מצוות התמיכה</b><br>${SupportHandler.escapeHtml(preview)}</div><div style="clear:both"></div>`,
            });

        },

        load() {

            if (!this.messages.length)
                this.loading = true;

            axiosV2('support/messages').then(res => {

                let rows = (res && res.data && res.data.messages) || [];

                rows.forEach(row => this.mergeMessage(row));

                this.loading = false;

                if (this.isOpen)
                    this.markSeen();

            }).catch(() => { this.loading = false; });

        },

        //rows are merged by id: a poll refreshes the delivery state of bubbles already shown, a
        //socket push adds the new one, and neither ever duplicates
        mergeMessage(row) {

            if (!row)
                return;

            let existing = row.id ? this.messages.find(m => m.id == row.id) : null;

            if (existing) {

                Object.keys(row).forEach(key => {
                    if (key != 'attachmentUrl' || row[key])
                        this.$set(existing, key, row[key]);
                });

                return existing;

            }

            let message = Object.assign({ sending: false, failed: false, localKey: null }, row);

            this.messages.push(message);

            this.scrollToBottom();

            return message;

        },

        markSeen() {

            if (!this.messages.some(m => m.fromSupport && !m.seenByTenant))
                return;

            this.messages.forEach(m => { if (m.fromSupport) m.seenByTenant = true; });

            this.$store.state.supportUnread = 0;

            apiPost('support/markSeen', {}, true).catch(() => { });

        },

        //the socket relay: {tenantId, msgType:'supportMessage', supportMessage:<row>}
        onSocketMessage(data) {

            let row = data && data.supportMessage;

            if (!row)
                return;

            let isNew = !this.messages.find(m => m.id == row.id);

            this.mergeMessage(row);

            if (this.isOpen) {
                this.markSeen();
                return;
            }

            if (!row.fromSupport || !isNew)
                return;

            this.$store.state.supportUnread = (this.$store.state.supportUnread || 0) + 1;

            this.toastSupportMessage(row);

        },

        scrollToBottom() {

            this.$nextTick(() => {
                let container = this.$refs.messageContainer;
                if (container)
                    container.scrollTop = container.scrollHeight;
            });

        },

        autoGrow() {

            let input = this.$refs.input;

            if (!input)
                return;

            input.style.height = 'auto';
            input.style.height = Math.min(140, input.scrollHeight) + 'px';

        },

        //the visible screen, without this modal and its backdrop
        captureScreenshot() {

            if (this.capturing)
                return;

            this.capturing = true;

            let modalElement = this.$el;

            SupportHandler.captureViewport(element =>
                element === modalElement || (element.classList && element.classList.contains('modal-backdrop'))
            ).then(dataUrl => {

                this.screenshot = dataUrl;

            }).catch(() => {

                this.AlertError('לא הצלחנו לצלם את המסך - אפשר להדביק צילום מסך במקום');

            }).finally(() => {

                this.capturing = false;

            });

        },

        onPaste(event) {

            let file = SupportHandler.imageFromPaste(event);

            if (!file)
                return;

            event.preventDefault();

            SupportHandler.fileToJpeg(file).then(dataUrl => { this.screenshot = dataUrl; }).catch(() => { });

        },

        onFilePicked(event) {

            let file = event.target.files && event.target.files[0];

            event.target.value = '';

            if (!file)
                return;

            SupportHandler.fileToJpeg(file)
                .then(dataUrl => { this.screenshot = dataUrl; })
                .catch(() => this.AlertError('הקובץ שנבחר אינו תמונה'));

        },

        send() {

            let text = this.text.trim();
            let screenshot = this.screenshot;

            if ((!text && !screenshot) || this.sending)
                return;

            //optimistic bubble; swapped for the stored row when the server answers
            let local = {
                localKey: 'local_' + Date.now(),
                id: null,
                message: text,
                attachmentUrl: screenshot,
                fromSupport: false,
                created: new Date(),
                sending: true,
                failed: false,
                gotWhatsappAck: false,
                sentViaWhatsapp: false,
                whatsappError: null,
            };

            this.messages.push(local);

            this.text = '';
            this.screenshot = null;
            this.sending = true;

            this.$nextTick(this.autoGrow);
            this.scrollToBottom();

            apiPost('support/send', {
                message: text,
                screenshot: screenshot,
                pageUrl: this.$route ? this.$route.fullPath : '',
            }, true).then(row => {

                let index = this.messages.indexOf(local);

                let stored = Object.assign({ sending: false, failed: false, localKey: local.localKey }, row);

                //the S3 copy and the local data url are the same picture - keep the one already
                //decoded so the bubble doesn't blink
                if (!stored.attachmentUrl && local.attachmentUrl)
                    stored.attachmentUrl = local.attachmentUrl;

                if (index > -1)
                    this.messages.splice(index, 1, stored);
                else
                    this.messages.push(stored);

                //the whatsapp delivery ack arrives over the socket keyed by messageId
                if (stored.messageId)
                    this.$handlers.whatsappHandler.messagesAwaitingAck[stored.messageId] = stored;

            }).catch(() => {

                local.sending = false;
                local.failed = true;

                this.AlertError('שליחת ההודעה נכשלה, נסו שוב');

            }).finally(() => {

                this.sending = false;

                this.$nextTick(() => {
                    if (this.$refs.input)
                        this.$refs.input.focus();
                });

            });

        },

    }

}

</script>
<style>
.supportModal .modal-dialog {
    max-width: 580px;
}

.supportModal .modal-content {
    border: none;
    border-radius: 12px;
    overflow: hidden;
}

.supportModal .modal-body {
    padding: 0 !important;
}

.supportChat {
    direction: rtl;
    text-align: right;
    display: flex;
    flex-direction: column;
    background: #f6f7fb;
}

/* header */

.supportChatHeader {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    background: white;
    border-bottom: solid 1px #e8e8ef;
}

.supportChatHeaderIcon {
    width: 44px;
    height: 44px;
    border-radius: 100%;
    background: #f2edfb;
    color: #7d5fc4;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
}

.supportChatHeaderText {
    flex: 1;
    min-width: 0;
}

.supportChatTitle {
    color: #2a3142;
    font-weight: 600;
    font-size: 17px;
    letter-spacing: 0.5px;
}

.supportChatSubtitle {
    color: #888;
    font-size: 12px;
    margin-top: 2px;
}

.supportChatClose {
    flex: none;
    margin: 0;
}

/* messages */

.supportChatMessages {
    height: 55vh;
    max-height: 520px;
    min-height: 260px;
    overflow-y: auto;
    padding: 18px 16px 8px 16px;
}

.supportChatCenter {
    text-align: center;
    padding: 40px 0;
    color: #999;
    font-size: 22px;
}

.supportChatEmpty {
    text-align: center;
    padding: 40px 30px;
    color: #555;
}

.supportChatEmptyIcon {
    font-size: 40px;
    margin-bottom: 10px;
}

.supportChatEmptyTitle {
    font-weight: 600;
    font-size: 16px;
    color: #2a3142;
    margin-bottom: 8px;
}

.supportChatEmptyText {
    font-size: 13px;
    line-height: 1.7;
    color: #777;
}

.supportBubbleRow {
    display: flex;
    margin-bottom: 12px;
}

/* the business's own messages sit on the right, like its outgoing whatsapp bubbles elsewhere */
.supportBubbleRow.fromUser {
    justify-content: flex-start;
}

.supportBubbleRow.fromSupport {
    justify-content: flex-end;
}

.supportBubble {
    max-width: 80%;
    border-radius: 12px;
    padding: 8px 12px 6px 12px;
    font-size: 14px;
    line-height: 1.6;
    color: #2a3142;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.supportBubbleRow.fromUser .supportBubble {
    background: #e9e2f8;
    border-top-right-radius: 3px;
}

.supportBubbleRow.fromSupport .supportBubble {
    background: white;
    border-top-left-radius: 3px;
}

.supportBubbleSender {
    font-size: 11px;
    font-weight: 600;
    color: #7d5fc4;
    margin-bottom: 3px;
}

.supportBubbleSender i {
    margin-left: 3px;
}

.supportBubbleText {
    white-space: pre-wrap;
    word-break: break-word;
}

.supportBubbleImage {
    display: block;
    margin-bottom: 6px;
}

.supportBubbleImage img {
    max-width: 100%;
    max-height: 220px;
    border-radius: 8px;
    border: solid 1px #ddd;
    background: white;
    display: block;
}

.supportBubbleMeta {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: flex-end;
    font-size: 11px;
    color: #999;
    margin-top: 2px;
    direction: ltr;
}

.supportBubbleStatus {
    font-size: 11px;
}

.supportStatusDelivered {
    color: #4fc3f7;
}

.supportStatusFailed {
    color: #b82e24;
}

.supportStatusSystem {
    color: #999;
    cursor: help;
}

/* composer */

.supportChatComposer {
    background: white;
    border-top: solid 1px #e8e8ef;
    padding: 10px 14px 8px 14px;
}

.supportScreenshotPreview {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    margin-bottom: 8px;
    border: solid 1px #e8e8ef;
    border-radius: 8px;
    background: #f6f7fb;
}

.supportScreenshotPreview img {
    height: 48px;
    width: auto;
    max-width: 90px;
    border-radius: 4px;
    border: solid 1px #ddd;
    background: white;
    object-fit: cover;
}

.supportScreenshotLabel {
    flex: 1;
    font-size: 13px;
    color: #555;
}

.supportScreenshotRemove {
    cursor: pointer;
    color: #999;
    padding: 4px 8px;
}

.supportScreenshotRemove:hover {
    color: #b82e24;
}

.supportComposerRow {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    border: solid 2px #ab8ce4;
    border-radius: 10px;
    padding: 4px 6px 4px 4px;
    background: white;
}

.supportComposerRow textarea {
    flex: 1;
    border: none !important;
    outline: none !important;
    resize: none;
    padding: 8px 6px;
    font-size: 14px;
    line-height: 1.5;
    max-height: 140px;
    background: transparent;
    font-family: inherit;
}

.supportComposerIconBtn {
    flex: none;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #7d5fc4;
    font-size: 16px;
    cursor: pointer;
}

.supportComposerIconBtn:hover,
.supportComposerIconBtn.active {
    background: #f2edfb;
}

.supportComposerIconBtn:disabled {
    cursor: default;
}

.supportSendBtn {
    flex: none;
    width: 40px;
    height: 36px;
    padding: 0 !important;
    border-radius: 8px !important;
    font-size: 15px;
}

.supportSendBtn i {
    transform: scaleX(-1);
}

.supportComposerHint {
    font-size: 11px;
    color: #aaa;
    margin-top: 6px;
    text-align: center;
}

@media (max-width: 575px) {

    .supportModal .modal-dialog {
        margin: 6px;
    }

    .supportChatMessages {
        height: 60vh;
    }

    .supportComposerHint {
        display: none;
    }

}
</style>
